import { useMemo, useState } from 'react';
import { X, ArrowRight, ShieldAlert } from '../../lib/icons';
import { toast } from 'sonner';
import { creerOrdreTransfert, estBlocageFiliere } from '../../lib/dataProvider';
import { fmt } from '../shared/badges';
import { useAuth } from '../../lib/auth';
import { estAdmin } from '../../lib/permissions';

const field =
  'mt-1.5 h-10 w-full border border-slate-300 bg-slate-50 px-3 font-data text-sm text-slate-900 outline-none transition-colors duration-150 focus:border-slate-900 focus:bg-white';
const label = 'text-xs font-bold uppercase tracking-[0.1em] text-slate-500';

// Étape 1 : création de l'ordre de transfert (théorique). Aucun stock mouvementé ici.
export const TransfertDialog = ({ cuves, sourceId, onClose, onDone }) => {
  const [source, setSource] = useState(sourceId ?? '');
  const [cible, setCible] = useState('');
  const [qte, setQte] = useState('');
  const [erreur, setErreur] = useState('');

  const src = cuves.find((c) => c.id === source);
  const dst = cuves.find((c) => c.id === cible);
  const croise = useMemo(() => src && dst && src.famille !== dst.famille, [src, dst]);

  const { user } = useAuth();
  const admin = estAdmin(user?.role);
  const submit = async (e, override = false) => {
    e?.preventDefault?.();
    const { error, ordre } = await creerOrdreTransfert({
      sourceId: source,
      cibleId: cible,
      quantite: qte,
      utilisateur: user?.name,
      override,
    });
    if (error) {
      if (
        !override &&
        estBlocageFiliere(error) &&
        admin &&
        window.confirm(`${error}\n\nOVERRIDE ADMINISTRATEUR : forcer quand même ? L'opération sera tracée dans le journal d'audit.`)
      )
        return submit(null, true);
      setErreur(error);
      toast.error(error);
      return;
    }
    toast.success(`Ordre ${ordre.numero} créé — imprimez-le pour l'atelier, les stocks bougeront à la validation du réel`);
    onDone(ordre);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" data-testid="transfert-dialog">
      <form onSubmit={submit} className="w-full max-w-lg border border-slate-200 bg-white">
        <div className="flex items-start justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <h2 className="font-heading text-lg font-black tracking-tight text-slate-900">
              Ordre de transfert inter-cuves
            </h2>
            <p className="font-data text-[11px] uppercase tracking-[0.1em] text-slate-500">
              Étape 1/2 — planification (aucun stock mouvementé)
            </p>
          </div>
          <button
            type="button"
            data-testid="transfert-close"
            onClick={onClose}
            className="border border-slate-200 p-2 text-slate-500 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
          >
            <X size={16} />
          </button>
        </div>

        <div className="space-y-4 px-5 py-4">
          <div className="grid grid-cols-[1fr_auto_1fr] items-end gap-3">
            <div>
              <label className={label}>Cuve source</label>
              <select data-testid="transfert-source" value={source} onChange={(e) => { setSource(e.target.value); setErreur(''); }} className={field}>
                <option value="">— Choisir —</option>
                {cuves.map((c) => (
                  <option key={c.id} value={c.id}>
                    {`${c.nom} — ${c.famille === 'lampante' ? 'Lampante' : 'Nobles'} — ${Math.round(c.volume).toLocaleString('fr-FR')} ${c.unite}`}
                  </option>
                ))}
              </select>
            </div>
            <ArrowRight size={16} className="mb-3 text-slate-400" />
            <div>
              <label className={label}>Cuve de destination</label>
              <select data-testid="transfert-cible" value={cible} onChange={(e) => { setCible(e.target.value); setErreur(''); }} className={field}>
                <option value="">— Choisir —</option>
                {cuves.map((c) => (
                  <option key={c.id} value={c.id}>
                    {`${c.nom} — ${c.famille === 'lampante' ? 'Lampante' : 'Nobles'} — reste ${Math.round(c.capacite - c.volume).toLocaleString('fr-FR')} ${c.unite}`}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {croise && (
            <p data-testid="transfert-blocage" className="flex items-start gap-2 border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
              <ShieldAlert size={14} className="mt-0.5 flex-shrink-0" />
              <span>
                <span className="font-bold uppercase tracking-[0.06em]">Affectation croisée bloquée</span> —
                impossible de transférer entre la filière nobles et la filière lampante.{' '}
                {admin ? 'En tant qu’Administrateur, la validation vous proposera un override tracé.' : 'Le système refusera l’opération.'}
              </span>
            </p>
          )}

          <div>
            <label className={label}>Quantité théorique à transférer {src ? `(${src.unite})` : ''}</label>
            <input
              data-testid="transfert-quantite"
              type="number"
              min="0"
              value={qte}
              onChange={(e) => { setQte(e.target.value); setErreur(''); }}
              className={field}
            />
            {src && (
              <p className="mt-1.5 font-data text-[11px] text-slate-500">
                Disponible dans {src.nom} : {fmt(src.volume)} {src.unite} — CMP source {fmt(src.cmp, 3)} TND/kg
              </p>
            )}
            {src && dst && Number(qte) > 0 && (
              <p data-testid="transfert-apercu-cmp" className="mt-2 border-l-4 border-slate-900 bg-slate-50 px-3 py-2 font-data text-[11px] text-slate-700">
                Valorisation de l'apport à la validation : {fmt(Number(qte) * (Number(src.cmp) || 0), 3)} TND
                ({fmt(qte, 1)} kg × CMP source {fmt(src.cmp, 3)}). Le CMP de {src.nom} reste inchangé ;
                celui de {dst.nom} sera recalculé sur son stock financier.
              </p>
            )}
          </div>

          {erreur && (
            <p data-testid="transfert-erreur" className="border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {erreur}
            </p>
          )}
        </div>

        <div className="flex gap-3 border-t border-slate-200 px-5 py-4">
          <button
            type="submit"
            data-testid="transfert-valider"
            disabled={croise && !admin}
            className={`h-11 flex-1 font-heading text-sm font-bold uppercase tracking-[0.08em] text-white transition-colors duration-150 ${
              croise && !admin ? 'cursor-not-allowed bg-slate-300' : croise ? 'bg-red-700 hover:bg-red-800' : 'bg-slate-900 hover:bg-slate-700'
            }`}
          >
            {croise && admin ? 'Forcer l’ordre (override admin)' : 'Créer l’ordre de transfert'}
          </button>
          <button
            type="button"
            data-testid="transfert-annuler"
            onClick={onClose}
            className="h-11 border border-slate-300 px-5 text-sm font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-100"
          >
            Annuler
          </button>
        </div>
      </form>
    </div>
  );
};
